import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { ButtonComponent, IconButtonComponent, IconComponent, TooltipDirective } from '@mode/capra';
import { ColorsModalFacade, FieldFormatModalService } from '@mode/charts/contract';
import { SharedUiModule, SplitValuePipe } from '@mode/shared/ui';
import { ContextMenuModule } from '@perfectmemory/ngx-contextmenu';
import { DndModule } from 'ngx-drag-drop';
import { AutoUpdateButtonComponent } from './auto-update/auto-update-button.component';
import { AutoUpdateMessageComponent } from './auto-update/auto-update-message.component';
import { BinSettingsQuickChartsComponent } from './bin-settings-quick-charts/bin-settings-quick-charts.component';
import { BinningModalService } from './binning-modal/binning-modal.service';
import { BackgroundSettingsToggleComponent } from './colors/background-settings-toggle/background-settings-toggle.component';
import { ColorFlyoutMenuService } from './colors/color-swatch-flyout/color-flyout-menu.service';
import { ColorSwatchFlyoutComponent } from './colors/color-swatch-flyout/color-swatch-flyout.component';
import { ColorValuesComponent } from './colors/color-values/color-values.component';
import { ColorsModalService } from './colors/colors-modal/colors-modal.service';
import { ConfirmationDialogComponent } from './colors/confirmation-dialog/confirmation-dialog.component';
import { FieldAndPaletteSelectorComponent } from './colors/field-and-palette-selector/field-and-palette-selector.component';
import { SequentialDivergentColorPickerComponent } from './colors/sequential-divergent-color-picker/sequential-divergent-color-picker.component';
import { ConditionalFormattingDialogComponent } from './conditional-formatting-dialog/conditional-formatting-dialog.component';
import { ConditionalFormattingModalComponentFacade } from './conditional-formatting-dialog/conditional-formatting-dialog.component.facade';
import { DataLimitsModalComponent } from './data-limits-modal/data-limits-modal.component';
import { EncodingBuilderHistogramsComponent } from './encoding-builder-histogram/encoding-builder-histogram.component';
import { EncodingBuilderVegasComponent } from './encoding-builder-vegas/encoding-builder-vegas.component';
import { EncodingDropzoneComponent } from './encoding-dropzone/encoding-dropzone.component';
import { EncodingFieldAllComponent } from './encoding-field-all/encoding-field-all.component';
import { EncodingFieldFilterComponent } from './encoding-field-filter/encoding-field-filter.component';
import { EncodingFieldMeasureNamesComponent } from './encoding-field-measure-names/encoding-field-measure-names.component';
import { EncodingFieldMultipleComponent } from './encoding-field-multiple/encoding-field-multiple.component';
import { EncodingFieldSingleComponent } from './encoding-field-single/encoding-field-single.component';
import { EncodingPillBaseComponent } from './encoding-field/encoding-pill-base.component';
import { CartesianBuilderCommonComponent } from './encoding-quick-charts/cartesian-builder-common/cartesian-builder-common.component';
import { ColorPaletteAssignmentComponent } from './encoding-quick-charts/color-palette-assignment/color-palette-assignment.component';
import { ComboBuilderComponent } from './encoding-quick-charts/combo-builder/combo-builder.component';
import { GroupedBuilderComponent } from './encoding-quick-charts/grouped-builder/grouped-builder.component';
import { PivotBuilderComponent } from './encoding-quick-charts/pivot-builder/pivot-builder.component';
import { RadialBuilderComponent } from './encoding-quick-charts/radial-builder/radial-builder.component';
import { StackedBuilderComponent } from './encoding-quick-charts/stacked-builder/stacked-builder.component';
import { FilterEncodingComponent } from './filter-encoding/filter-encoding.component';
import { FilterModalService } from './filter-modal/filter-modal.service';
import { ChartsFormatBuilderModule } from './format-builder/format-builder.module';
import { ChannelDropZoneComponent } from './marks-card/channel-drop-zone/channel-drop-zone.component';
import { BaselineChannelComponent } from './marks-card/channels/baseline-channel.component';
import { ChannelComponent } from './marks-card/channels/channel.component';
import { ColorChannelComponent } from './marks-card/channels/color-channel.component';
import { TextChannelComponent } from './marks-card/channels/text-channel.component';
import { EditTextModalComponent } from './marks-card/edit-text-modal/edit-text-modal.component';
import { LayerCardComponent } from './marks-card/layer-card/layer-card.component';
import { LayerOptionsMenuComponent } from './marks-card/layer-options/layer-options-menu.component';
import { LayerTypeSelectorComponent } from './marks-card/layer-type-selector/layer-type-selector.component';
import { MarksPanelComponent } from './marks-card/marks-panel/marks-panel.component';
import { LabeledFieldListMenuComponent } from './menu/labeled-field-list-menu.component';
import { RankPanelComponent } from './rank-panel/rank-panel.component';
import { SharedAxisEncodingComponent } from './shared-axis-encoding/shared-axis-encoding.component';
import { SortModalComponent } from './sort-modal';
import { SortModalListComponent } from './sort-modal/list/list.component';
import { TableCalcDialogComponent } from './table-calc-dialog';
import { TextFormattingModalService } from './text-formatting-modal/text-formatting-modal.service';
import { AxisFitComponent } from './toolbar/axis-fit/axis-fit.component';
import { TotalsButtonComponent } from './toolbar/totals-button/totals-button.component';
import { TypeaheadAttrsListComponent } from './typeahead-attrs-list/typeahead-attrs-list.component';

@NgModule({
  imports: [
    BaselineChannelComponent,
    ButtonComponent,
    ChannelComponent,
    ChannelDropZoneComponent,
    ColorChannelComponent,
    CommonModule,
    ContextMenuModule.forRoot(),
    DndModule,
    DragDropModule,
    EncodingFieldSingleComponent,
    EncodingFieldAllComponent,
    EncodingFieldFilterComponent,
    EncodingFieldMeasureNamesComponent,
    EncodingFieldMultipleComponent,
    EncodingPillBaseComponent,
    FormsModule,
    IconButtonComponent,
    IconComponent,
    LabeledFieldListMenuComponent,
    LayerCardComponent,
    LayerOptionsMenuComponent,
    MatLegacyButtonModule,
    MatLegacyDialogModule,
    MatLegacyListModule,
    MatExpansionModule,
    MatLegacyMenuModule,
    RankPanelComponent,
    ChartsFormatBuilderModule,
    ScrollingModule,
    SharedUiModule,
    SplitValuePipe,
    TextChannelComponent,
    TooltipDirective,
    MatLegacyAutocompleteModule,
    ReactiveFormsModule,
    ColorSwatchFlyoutComponent,
    FieldAndPaletteSelectorComponent,
    SequentialDivergentColorPickerComponent,
    ColorValuesComponent,
    ConfirmationDialogComponent,
    BackgroundSettingsToggleComponent,
    ConditionalFormattingDialogComponent,
    LayerTypeSelectorComponent,
    TypeaheadAttrsListComponent,
    ColorPaletteAssignmentComponent,
  ],
  declarations: [
    AutoUpdateButtonComponent,
    AutoUpdateMessageComponent,
    CartesianBuilderCommonComponent,
    ComboBuilderComponent,
    EncodingBuilderVegasComponent,
    MarksPanelComponent,
    FilterEncodingComponent,
    AxisFitComponent,
    TotalsButtonComponent,
    EditTextModalComponent,
    EncodingDropzoneComponent,
    SharedAxisEncodingComponent,
    SortModalComponent,
    TableCalcDialogComponent,
    SortModalListComponent,
    DataLimitsModalComponent,
    GroupedBuilderComponent,
    RadialBuilderComponent,
    StackedBuilderComponent,
    PivotBuilderComponent,
    EncodingBuilderHistogramsComponent,
    BinSettingsQuickChartsComponent,
  ],
  exports: [EncodingDropzoneComponent],
  providers: [
    FilterModalService,
    ConditionalFormattingModalComponentFacade,
    ColorFlyoutMenuService,
    { provide: ColorsModalFacade, useClass: ColorsModalService },
    { provide: FieldFormatModalService, useClass: TextFormattingModalService },
    { provide: BinningModalService },
  ],
})
export class ChartsFeatureEncodingModule {}
